// @@DASH_OPEN_INVOICES_v84@@ Client portal Dashboard — "Open invoices" card.
// Reuses GET /api/portal/invoices (v83; server-scoped + 15-min cached) — no
// new endpoint. Renders nothing when the account isn't linked to billing or
// Zoho is unreachable (the Account balance tile already says so).
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';

function money(n, ccy) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'USD' }).format(Number(n));
  } catch (e) {
    return `$${Number(n).toFixed(2)}`;
  }
}
// YYYY-MM-DD -> local date (never new Date('YYYY-MM-DD'): UTC shows the day before).
function fmtYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return '—';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
}
const STATUS_LABEL = { open: 'Open', overdue: 'Overdue', partially_paid: 'Partially paid' };
const OPENISH = new Set(['open', 'overdue', 'partially_paid']);
const safeName = (s) => String(s || '').replace(/[^A-Za-z0-9._-]/g, '_');
const TOP_N = 5;

export default function OpenInvoicesCard({ clientId, onViewAll }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!clientId) return;
    let live = true;
    setData(null); setMsg('');
    apiFetch(`/api/portal/invoices?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setData({ available: false }); });
    return () => { live = false; };
  }, [clientId]);

  const sum = useMemo(() => {
    if (!data || !data.available) return null;
    const open = (data.invoices || []).filter((i) => OPENISH.has(i.status));
    const overdue = open.filter((i) => i.status === 'overdue');
    const add = (list) => list.reduce((t, i) => t + (Number(i.balance) || 0), 0);
    const top = open.slice().sort((a, b) => {
      const ao = a.status === 'overdue' ? 0 : 1, bo = b.status === 'overdue' ? 0 : 1;
      if (ao !== bo) return ao - bo;
      return String(a.due_date || '9999').localeCompare(String(b.due_date || '9999'));
    }).slice(0, TOP_N);
    return { count: open.length, balance: add(open), overdueCount: overdue.length, overdue: add(overdue), top };
  }, [data]);

  async function download(number) {
    setBusy(number); setMsg('');
    try {
      const res = await apiFetch(`/api/portal/invoices/pdf?client_id=${encodeURIComponent(clientId)}&number=${encodeURIComponent(number)}`);
      if (!res.ok) { setMsg('Could not download the invoice right now. Please try again shortly.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const desig = safeName(data && data.client && data.client.designation_code);
      a.href = url; a.download = 'XYZ-Invoice-' + safeName(number) + (desig ? '-' + desig : '') + '.pdf';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      setMsg('Could not download the invoice right now. Please try again shortly.');
    } finally { setBusy(''); }
  }

  if (!data) {
    return (
      <div className="cp-card cp-oi">
        <div className="cp-card-head"><h2>Open invoices</h2></div>
        <div className="cp-empty">Loading…</div>
      </div>
    );
  }
  if (!sum) return null; // not linked / unreachable — balance tile already explains

  const ccy = data.currency_code || 'USD';
  return (
    <div className="cp-card cp-oi">
      <div className="cp-card-head">
        <h2>Open invoices</h2>
        {onViewAll && <button className="cp-btn-sm" onClick={onViewAll}>View all</button>}
      </div>
      {sum.count === 0 ? (
        <div className="cp-empty">No open invoices — you’re all paid up.</div>
      ) : (
        <>
          <div className="cp-oi-stats">
            <div><span className="cp-k">Open</span><span className="cp-oi-v">{sum.count}</span></div>
            <div><span className="cp-k">Balance due</span><span className="cp-oi-v">{money(sum.balance, ccy)}</span></div>
            <div>
              <span className="cp-k">Overdue</span>
              <span className={sum.overdue > 0 ? 'cp-oi-v cp-oi-late' : 'cp-oi-v'}>{money(sum.overdue, ccy)}</span>
              {sum.overdueCount > 0 && <span className="cp-sub">{sum.overdueCount} invoice{sum.overdueCount === 1 ? '' : 's'}</span>}
            </div>
          </div>
          {msg && <div className="cp-inv-msg" style={{ margin: '0 20px 12px' }}>{msg}</div>}
          <div className="cp-inv-scroll">
            <table className="cp-table">
              <thead><tr><th>Invoice #</th><th>Due</th><th style={{ textAlign: 'right' }}>Balance</th><th>Status</th><th /></tr></thead>
              <tbody>
                {sum.top.map((i) => (
                  <tr key={i.number}>
                    <td>{i.number}</td>
                    <td>{fmtYmd(i.due_date)}</td>
                    <td style={{ textAlign: 'right' }}>{money(i.balance, ccy)}</td>
                    <td><span className={'cp-inv-badge cp-inv-' + i.status}>{STATUS_LABEL[i.status] || i.status}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="cp-btn-sm" onClick={() => download(i.number)} disabled={busy === i.number}>
                        {busy === i.number ? '…' : 'PDF'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sum.count > TOP_N && onViewAll && (
            <div className="cp-oi-more"><button className="cp-oi-link" onClick={onViewAll}>+ {sum.count - TOP_N} more open invoice{sum.count - TOP_N === 1 ? '' : 's'}</button></div>
          )}
        </>
      )}
    </div>
  );
}
